<template>
  <div
    class="col-12 px-0"
  >
    <v-expansion-panel
      data-component-id="Document"
      :readonly="isAddMode"
      style="border: 1px solid #d7d7d7; border-radius: 10px; background-color: transparent;"
    >
      <v-expansion-panel-header
        class="flex-row-reverse"
        style="gap: 16px;"
        color="grey lighten-2"
      >
        <div
          class="
            d-flex justify-end
            text-right
          "
          style="gap: 16px;"
        >
          <v-btn
            icon
            :disabled="permission ? !canPerformAction(`EDIT_${permission}`) : false"
            @click.stop="handleUploadClick"
          >
            <v-icon class="justify-end">
              mdi-plus
            </v-icon>
          </v-btn>
        </div>
        <h3>{{ title }}</h3>
      </v-expansion-panel-header>

      <v-expansion-panel-content
        class="pa-5"
        color="transparent"
        style="border-bottom-right-radius: 10px; border-bottom-left-radius: 10px;"
      >
        <div
          v-if="files.length"
          class="d-flex flex-column"
          style="gap: 16px;"
        >
          <section
            v-for="(item, itemIndex) in files"
            :key="itemIndex"
            class="
              d-flex align-start align-md-center
              pa-0 col-12
            "
            style="gap: 32px;"
          >
            <v-list
              dense
              class="
                relative d-flex flex-grow-1 flex-column
                px-5
              "
              style="min-height: 90px; border-radius: 10px;"
            >
              <v-list-item class="pl-0 break-word flex-column flex-md-row align-start align-md-center">
                <v-btn
                  icon
                  :disabled="permission ? !canPerformAction(`EDIT_${permission}`) : false"
                  @click="toggleInlinePreview(item, itemIndex)"
                >
                  <v-icon>
                    {{ inlinePreviewIndexes.includes(itemIndex) ? 'mdi-menu-up' : 'mdi-menu-down' }}
                  </v-icon>
                </v-btn>

                <div class="d-flex flex-grow-1 align-center">
                  <v-list-item-avatar>
                    <v-icon color="primary">
                      mdi-file
                    </v-icon>
                  </v-list-item-avatar>

                  <v-list-item-content class="align-self-center">
                    <v-list-item-title
                      class=" "
                    >
                      <button
                        class="
                          px-0
                          body-1 font-family-saira font-weight-regular text-decoration-underline
                        "
                        text
                        :disabled="permission ? !canPerformAction(`VIEW_${permission}`) : false"
                        @click="openFile(item)"
                      >
                        {{ item.description }}
                      </button>
                    </v-list-item-title>
                  </v-list-item-content>
                </div>
                <v-list-item-action class="align-self-start align-md-center">
                  <v-select
                    id="tag"
                    v-model="item.tag"
                    :items="getFieldReferences('tag')"
                    dense
                    outlined
                    hide-details
                    :disabled="permission ? !canPerformAction(`EDIT_${permission}`) : false"
                    @input="(value) => handleSelect(value, files, itemIndex)"
                  />
                </v-list-item-action>
              </v-list-item>
              <!-- Inline preview below this item if selected -->
              <v-list-item
                v-if="inlinePreviewIndexes.includes(itemIndex)"
                class="pa-0"
                style="background: #f5f5f5;"
              >
                <div style="width:100%;">
                  <div class="d-flex justify-end">
                    <v-btn icon small @click="closeInlinePreview(itemIndex)">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </div>
                  <iframe
                    v-if="inlineGoogleViewerUrls[itemIndex]"
                    :key="inlineAutoRefreshKeys[itemIndex]"
                    :src="inlineGoogleViewerUrls[itemIndex]"
                    style="width:100%; height:400px; border:none;"
                    @load="onInlineIframeLoad(itemIndex)"
                  ></iframe>
                  <div v-if="!inlineIframeLoaded[itemIndex]" class="d-flex justify-center align-center" style="height: 400px;">
                    <v-progress-circular indeterminate color="primary" />
                  </div>
                </div>
              </v-list-item>
            </v-list>

            <section
              class="d-flex flex-column"
              style="gap: 16px;"
            >
              <v-btn
                icon
                :disabled="permission ? !canPerformAction(`EDIT_${permission}`) : false"
                @click="viewFile(item)"
              >
                <v-icon>
                  mdi-eye
                </v-icon>
              </v-btn>

              <v-btn
                icon
                :disabled="permission ? !canPerformAction(`EDIT_${permission}`) : false"
                @click="$emit('attachment-delete', itemIndex)"
              >
                <v-icon>
                  mdi-delete
                </v-icon>
              </v-btn>
            </section>
          </section>
        </div>
        <div
          v-else
          class="text-center"
        >
          Aucune information renseignée
        </div>

        <input
          ref="fileInput"
          type="file"
          name="file"
          accept=".pdf,.doc"
          hidden
          @change="handleFileSelect"
        >
      </v-expansion-panel-content>
    </v-expansion-panel>
    <v-dialog
      v-model="showDialog"
      max-width="1280"
    >
      <v-card
        min-height="100%"
        height="600px"
        max-height="100%"
      >
        <div
          v-if="!iframeLoaded"
          class="d-flex justify-center align-center fill-height col-12"
        >
          <v-progress-circular
            indeterminate
            color="primary"
          />
        </div>

        <v-card-text
          class="fill-height pa-0 d-flex align-center justify-center"
        >
          <iframe
            v-if="googleViewerUrl"
            :key="componentRendered"
            :src="googleViewerUrl"
            class="col-12 pa-0"
            :class="{ 'd-none' : !iframeLoaded }"
            style="
              height: 100%;
              border: none;
            "
            allow="autoplay"
            allowfullscreen
            @load="iframeLoaded = true"
          ></iframe>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import { requests } from '@/mixins/index';

export default {
  name: 'Document',
  props: {
    title: {
      type: String,
      default: null
    },
    collaboratorUuid: {
      type: String,
      default: null
    },
    items: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    permission: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      files: JSON.parse(JSON.stringify(this.items || [])),
      loading: { add: false },
      isAddMode: false,

      googleViewerUrl: null,
      showDialog: false,
      iframeLoaded: false,
      componentRendered: 0,

      inlinePreviewIndexes: [],
      inlineGoogleViewerUrls: {},
      inlineAutoRefreshKeys: {},
      inlineIframeLoaded: {}
    };
  },
  computed: {
    ...mapState(['references', 'focusMode']),
    ...mapGetters(['canPerformAction'])
  },
  watch: {
    items (value) {
      this.resetInlinePreview();

      this.files = JSON.parse(JSON.stringify(value || []));
      this.files.forEach((file, index) => {
        this.toggleInlinePreview(file, index);
      });
    }
  },
  mounted () {
    this.files.forEach((file, index) => {
      this.toggleInlinePreview(file, index);
    });
  },
  methods: {
    getFieldReferences (optionsByReference) {
      if (!optionsByReference) return [];

      return Object.entries(this.references[optionsByReference]).map(([key, value]) => ({ text: value, value: key }));
    },
    setAddMode (value) {
      this.isAddMode = value;

      if (value) {
        this.$emit('is-edit-mode', value);
      }
    },
    validateFile (file) {
      if (file.size > 200 * 1024 * 1024) {
        this.$store.commit('setDialog', {
          show: true,
          title: 'Erreur',
          text: 'Le fichier est trop volumineux (max 200MB)'
        });

        return false;
      }

      return true;
    },
    handleUploadClick () {
      this.$emit('is-edit-mode', true);

      this.$store.commit('setDialog', {
        show: true,
        title: 'Télécharger un document',
        text: 'Formats acceptés: PDF, DOC (max 200MB)',
        handleAccept: () => this.$refs.fileInput.click()
      });
    },
    async handleFileSelect (event) {
      this.loading.add = true;

      const file = event.target.files[0];
      if (!file) return;

      if (!this.validateFile(file)) return;

      this.files.push({
        description: file.name,
        type: file.type,
        size: file.size,
        url: URL.createObjectURL(file)
      });

      const { data: attachments } = await requests.uploadAttachment(event.target.files, this.collaboratorUuid);

      this.$emit('attachment-uploaded', attachments);

      event.target.value = '';

      this.loading.add = false;
    },
    async handleSelect (value, item, itemIndex) {
      item[itemIndex].tag = value;

      this.$emit('attachment-edit', item);
    },
    async openFile (file) {
      if (!file) return;

      const { data } = await requests.downloadAttachment(file.path);

      const response = await fetch(data, { method: 'GET' }).then(r => r).catch(() => null);

      if (!response) {
        console.info('Erreur lors du téléchargement');
        return;
      }

      const blob = await response.blob();

      const newBlob = new Blob([blob]);

      const blobUrl = window.URL.createObjectURL(newBlob);

      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = file.description;

      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      window.URL.revokeObjectURL(blobUrl);
    },
    async viewFile (file) {
      if (!file) return;

      // Get the file data
      const { data } = await requests.downloadAttachment(file.path);

      const response = await fetch(data, { method: 'GET' }).then(r => r).catch(() => null);

      if (!response) {
        console.info('Erreur lors du téléchargement');
        return;
      }

      this.googleViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(data)}&embedded=true`;

      this.iframeLoaded = false;
      this.showDialog = true;

      // Auto refresh until loaded
      const autoRefresh = () => {
        if (!this.iframeLoaded && this.showDialog) {
          this.componentRendered += 1;
          setTimeout(autoRefresh, 2000);
        }
      };

      // Start auto refresh
      setTimeout(autoRefresh, 2000);
    },
    async toggleInlinePreview (file, index) {
      if (this.inlinePreviewIndexes.includes(index)) {
        // Close preview
        this.inlinePreviewIndexes = this.inlinePreviewIndexes.filter(i => i !== index);
        this.inlineGoogleViewerUrls = { ...this.inlineGoogleViewerUrls, [index]: null };
        this.inlineIframeLoaded = { ...this.inlineIframeLoaded, [index]: false };
        return;
      }
      // Open preview
      if (!file) return;
      const { data } = await requests.downloadAttachment(file.path);
      const response = await fetch(data, { method: 'GET' }).then(r => r).catch(() => null);
      if (!response) {
        console.info('Erreur lors du téléchargement');
        return;
      }
      this.inlinePreviewIndexes.push(index);
      this.inlineGoogleViewerUrls = { ...this.inlineGoogleViewerUrls, [index]: null };
      this.inlineIframeLoaded = { ...this.inlineIframeLoaded, [index]: false };
      this.inlineAutoRefreshKeys = { ...this.inlineAutoRefreshKeys, [index]: (this.inlineAutoRefreshKeys[index] || 0) + 1 };

      setTimeout(() => {
        this.inlineGoogleViewerUrls = {
          ...this.inlineGoogleViewerUrls,
          [index]: `https://docs.google.com/viewer?url=${encodeURIComponent(data)}&embedded=true`
        };
        this.inlineAutoRefreshKeys = { ...this.inlineAutoRefreshKeys, [index]: (this.inlineAutoRefreshKeys[index] || 0) + 1 };

        // Scroll into view after DOM update
        this.$nextTick(() => {
          const el = this.$refs[`fileItem-${index}`];
          if (el && el[0]) el[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        });

        // Auto refresh until loaded
        const autoRefresh = () => {
          if (!this.inlineIframeLoaded[index] && this.inlinePreviewIndexes.includes(index)) {
            this.inlineAutoRefreshKeys = { ...this.inlineAutoRefreshKeys, [index]: (this.inlineAutoRefreshKeys[index] || 0) + 1 };
            setTimeout(autoRefresh, 2000);
          }
        };
        setTimeout(autoRefresh, 2000);
      }, 50);
    },
    onInlineIframeLoad (index) {
      this.inlineIframeLoaded = { ...this.inlineIframeLoaded, [index]: true };
    },
    closeInlinePreview (index) {
      this.inlinePreviewIndexes = this.inlinePreviewIndexes.filter(i => i !== index);
      this.inlineGoogleViewerUrls = { ...this.inlineGoogleViewerUrls, [index]: null };
      this.inlineIframeLoaded = { ...this.inlineIframeLoaded, [index]: false };
    },
    resetInlinePreview () {
      this.inlinePreviewIndexes = [];
      this.inlineGoogleViewerUrls = {};
      this.inlineAutoRefreshKeys = {};
      this.inlineIframeLoaded = {};
    }
  }
};
</script>
